import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNodeData } from "@/hooks/useNodeData";
import { useNodeRankings } from "@/hooks/useNodeRankings";
import { useNodeSelection } from "@/contexts/node-selection";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { NodeStatsGrid } from "./NodeStatsGrid";

export function NodeStatistics() {
  const { selectedNode } = useNodeSelection();
  const { data, isLoading, error } = useNodeData(selectedNode?.address || null);
  const { data: rankings } = useNodeRankings();

  const { data: marketData } = useQuery({
    queryKey: ["marketData"],
    queryFn: async () => {
      const { data } = await supabase
        .from('dusk_market_data')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(1)
        .single();
      return data;
    },
    refetchInterval: 60000,
  });

  const duskPrice = marketData?.current_price_usd || 0;

  if (!selectedNode || isLoading || error || !data) {
    return (
      <Card className="w-full md:w-1/2">
        <CardHeader>
          <CardTitle className="text-lg font-medium">Node Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center p-4">
            {!selectedNode ? "Please select a node" : 
             isLoading ? "Loading..." : 
             error ? "Error loading data" : 
             "No data available"}
          </div>
        </CardContent>
      </Card>
    );
  }

  const formattedReward = typeof data.currentReward === 'number' 
    ? data.currentReward.toLocaleString()
    : '0';

  const formattedStake = typeof data.stake === 'number'
    ? data.stake.toLocaleString()
    : '0';

  const formattedLockedAmt = typeof data.lockedAmt === 'number'
    ? data.lockedAmt.toLocaleString()
    : '0';

  const nodeRanking = rankings?.find(r => r.address === selectedNode.address);
  const rankDisplay = nodeRanking 
    ? `#${nodeRanking.rank}`
    : 'Calculating...';

  return (
    <Card className="w-full md:w-1/2">
      <CardHeader>
        <CardTitle className="text-lg font-medium">Node Statistics</CardTitle>
      </CardHeader>
      <CardContent>
        <NodeStatsGrid
          formattedStake={formattedStake}
          formattedReward={formattedReward}
          formattedLockedAmt={formattedLockedAmt}
          hardFaults={data.hardFaults}
          faults={data.faults}
          rankDisplay={rankDisplay}
          duskPrice={duskPrice}
          data={data}
        />
      </CardContent>
    </Card>
  );
}